/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useEffect, useState } from "react";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * A row this popup carries in its selection map. The issue members are present when the popup is
 * attaching issues rather than files; no index signature, so plain `SelectedFileItem[]` still
 * satisfies callbacks typed against it.
 */
type PopupFileRow = SelectedFileItem & {
  issueVersionId?: ObjectIdString;
  issueId?: ObjectIdString;
  /** Set on issue rows by the attach-issues flow; distinct from `folderItemSchema`'s `name`. */
  fileName?: string;
};
import { checkIfFilesAreAttachedForIssues } from "@Features/transmittals/utils/transmittalHelpers";

/**
 * Shared hook for FileManagerAsPopup logic that works on both web and mobile.
 * Contains: file selection management, validation, add/remove from selection list,
 * view mode toggle, space switching.
 *
 * Web-specific logic (modals, API calls for shortcuts/consume, toast notifications) remains in FileManagerAsPopup.
 */

interface UseFileManagerAsPopupSharedProps {
  // Callbacks
  handleFileSelectedButtonClick?(listOfSelectedFiles: never, inputValue?: string): void;
  handleFolderSelected?(folderData: never): void;

  // Configuration
  minimumFiles?: number;
  allowInput?: boolean;
  disableMultipleSelection?: boolean;
  isTransmittal?: boolean;
  addEmptyContainerToList?: boolean;
  createMerge?: boolean;
  createShortcut?: boolean;

  // Pre-selected data
  selectedFilesData?: PopupFileRow[];

  // Loading state setters (optional)
  setIsLoadingCreateButton?: (loading: boolean) => void;
  setIsLoadingAddFiles?: (loading: boolean) => void;

  // Space and view configuration
  workspaceId?: string;
  hasCDEPlan?: boolean;
  hasPMPlan?: boolean;
  allowViewToggle?: boolean;
}

function useFileManagerAsPopupShared({
  handleFileSelectedButtonClick,
  handleFolderSelected,
  minimumFiles,
  allowInput = false,
  disableMultipleSelection = false,
  isTransmittal = false,
  addEmptyContainerToList: _addEmptyContainerToList = false,
  createMerge = false,
  createShortcut: _createShortcut = false,
  selectedFilesData = [],
  setIsLoadingCreateButton,
  setIsLoadingAddFiles,
  workspaceId,
  hasCDEPlan = false,
  hasPMPlan = false,
  allowViewToggle = true,
}: UseFileManagerAsPopupSharedProps) {
  // Selection state
  const [listOfSelectedFiles, setListOfSelectedFiles] = useState<Record<string, PopupFileRow>>({});
  const [selectedFiles, setSelectedFiles] = useState<PopupFileRow[]>([]);
  const [selectedFolderData, setSelectedFolderData] = useState<PopupFileRow>({});

  // Button states
  const [AddToListDisabled, setAddToListDisabled] = useState(true);
  const [disableAttachButton, setDisableAttachButton] = useState(true);
  const [disableCreateShortcutButton, setDisableCreateShortcutButton] = useState(true);

  // Input state (for merge file name)
  const [inputValue, setInputValue] = useState("");

  // Issues state (for transmittals)
  const [issuesHavingNoFiles, setIssuesHavingNoFiles] = useState<PopupFileRow[]>([]);

  // View mode state: list view (true) vs folder structure view (false)
  // Default to list view if user has PM plan but not CDE plan
  const [toggleFilesAsList, setToggleFilesAsList] = useState(hasPMPlan && !hasCDEPlan);

  // Space switching state
  const [switchSpaceId, setSwitchSpaceId] = useState(workspaceId);
  const [showNoSpaceWarning, setShowNoSpaceWarning] = useState<boolean | null>(null);

  // Sync switchSpaceId when workspaceId prop changes
  useEffect(() => {
    if (workspaceId) {
      setSwitchSpaceId(workspaceId);
    }
  }, [workspaceId]);

  // Validate attach button state based on minimumFiles and input
  useEffect(() => {
    let disable: boolean;

    // If folder selection is enabled and a folder (or space root) is selected, enable the button
    if (
      handleFolderSelected &&
      selectedFolderData &&
      (selectedFolderData._id || selectedFolderData.id || selectedFolderData.spaceId)
    ) {
      disable = false;
    }
    // Otherwise, check file selection
    else {
      const fileCount = Object.keys(listOfSelectedFiles).length;
      const filesOk = minimumFiles === undefined ? fileCount > 0 : fileCount >= minimumFiles;
      const inputOk = !allowInput || !!inputValue.trim();
      disable = !(filesOk && inputOk);
    }

    setDisableAttachButton(disable);

    // Check for issues without files (transmittal mode)
    if (isTransmittal && Object.keys(listOfSelectedFiles).length) {
      const issuesWithNoFiles = checkIfFilesAreAttachedForIssues(listOfSelectedFiles) as PopupFileRow[];
      setIssuesHavingNoFiles(issuesWithNoFiles);
    }
  }, [
    listOfSelectedFiles,
    inputValue,
    allowInput,
    minimumFiles,
    isTransmittal,
    handleFolderSelected,
    selectedFolderData,
  ]);

  // Validate "Add to Selection" button state
  useEffect(() => {
    if (disableMultipleSelection) {
      // Only allow adding if exactly 1 file selected and no files in list yet
      if (selectedFiles.length === 1 && Object.keys(listOfSelectedFiles).length < 1) {
        setAddToListDisabled(false);
      } else {
        setAddToListDisabled(true);
      }
    } else if (selectedFiles.length) {
      setAddToListDisabled(false);
    } else {
      setAddToListDisabled(true);
    }
  }, [selectedFiles.length, listOfSelectedFiles, disableMultipleSelection]);

  // Handle pre-selected files when createMerge is enabled
  useEffect(() => {
    if (createMerge && selectedFilesData.length) {
      handleAddToSelection(null, selectedFilesData);
    }
  }, [createMerge, selectedFilesData]);

  /**
   * Handle file selection from FileManager grid
   * Accepts the selected files array from the file manager
   * Folder filtering is already handled by the file manager component (filesOnly prop)
   */
  const handleFileSelectionCallback = (data: PopupFileRow[]) => {
    // Filter out folders if any are included (safety check)
    const filesOnly = Array.isArray(data)
      ? data.filter((item: PopupFileRow) => item.resourceType !== "RESOURCE_TYPE_FOLDER")
      : [];
    setSelectedFiles(filesOnly);
  };

  /**
   * Add selected files to the selection list
   * Handles different resource types (files, issues, tasks, estimates, merged files, embeds)
   */
  const handleAddToSelection = (e: React.SyntheticEvent | null, selectedFilesList?: PopupFileRow[]) => {
    let selectedFilesCopy: PopupFileRow[];
    if (selectedFilesList) {
      selectedFilesCopy = selectedFilesList;
      setSelectedFiles(selectedFilesList);
    } else {
      selectedFilesCopy = [...selectedFiles];
    }

    const selectedFilesToObj: Record<string, PopupFileRow> = { ...listOfSelectedFiles };

    selectedFilesCopy.forEach((file: PopupFileRow) => {
      let key: string | undefined;

      // External volume files (have 'dev' property)
      if (Object.hasOwn(file, "dev")) {
        key = `${file.filterPath}${file.name}`;
      }
      // Issues
      else if (file.resourceType === "RESOURCE_TYPE_ISSUE") {
        key = file.issueVersionId;
      }
      // Tasks
      else if (file.resourceType === "RESOURCE_TYPE_TASKS") {
        key = file.taskId;
      }
      // Estimates and Merged files - use id
      else if (file.resourceType === "RESOURCE_TYPE_ESTIMATE" || file.resourceType === "RESOURCE_TYPE_MERGED_FILE") {
        key = file.id;
      }
      // Regular files, embeds, and any other type - use fileVersion or id or _id
      else {
        key = file.fileVersion || file.id || file._id;
      }

      // Always add the file if we have any valid key, or use index as last resort
      if (key) {
        selectedFilesToObj[key] = file;
      } else {
        // Fallback: use a generated key to ensure file is added
        const fallbackKey = `file_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        selectedFilesToObj[fallbackKey] = file;
      }
    });

    setListOfSelectedFiles(selectedFilesToObj);

    if (e) {
      e.stopPropagation();
    }
  };

  /**
   * Remove a file from the selection list
   */
  const handleRemoveOfSelectedFile = (fileData: PopupFileRow) => {
    const tempListOfSelectedFiles: Record<string, PopupFileRow> = { ...listOfSelectedFiles };

    if (fileData.resourceType === "RESOURCE_TYPE_MERGED_FILE" || fileData.resourceType === "RESOURCE_TYPE_ESTIMATE") {
      delete tempListOfSelectedFiles[(fileData.id || fileData._id)!];
    } else {
      delete tempListOfSelectedFiles[(fileData.fileVersion || fileData.fileVersionId || fileData.id || fileData._id)!];
    }

    // Handle external volume files
    if (Object.hasOwn(fileData, "dev")) {
      delete tempListOfSelectedFiles[fileData.name!];
    }

    setListOfSelectedFiles(tempListOfSelectedFiles);
  };

  /**
   * Clear all selected files
   */
  const clearAllSelected = () => {
    setListOfSelectedFiles({});
  };

  /**
   * Handle the "Attach" button click - submits selected files
   */
  const handleFileSelectedButtonClickCallback = () => {
    if (typeof setIsLoadingCreateButton === "function") {
      setIsLoadingCreateButton(true);
    }
    if (typeof setIsLoadingAddFiles === "function") {
      setIsLoadingAddFiles(true);
    }
    if (handleFileSelectedButtonClick) {
      handleFileSelectedButtonClick(listOfSelectedFiles as never, inputValue);
    }
  };

  /**
   * Handle folder selection callback
   */
  const handleFolderSelectionCallback = (data: PopupFileRow) => {
    setSelectedFolderData(data);
    if (data.folderType === 0) {
      setDisableCreateShortcutButton(false);
    }
  };

  /**
   * Handle folder selected button click
   */
  const handleFolderSelectedCallback = () => {
    setDisableAttachButton(true);
    if (handleFolderSelected) {
      handleFolderSelected(selectedFolderData as never);
    }
  };

  /**
   * Remove issues that have no files attached (for transmittal cancel)
   */
  const removeIssuesWithNoFiles = () => {
    const tempListOfSelectedFiles: Record<string, PopupFileRow> = { ...listOfSelectedFiles };
    issuesHavingNoFiles.forEach((issue: PopupFileRow) => delete tempListOfSelectedFiles[issue.issueVersionId!]);
    setListOfSelectedFiles(tempListOfSelectedFiles);
    setIssuesHavingNoFiles([]);
  };

  /**
   * Accept files for issues that have no files (adds parent file to selection)
   */
  const acceptFilesForIssues = (e: React.SyntheticEvent | null) => {
    const filesUnderWhichIssuesAreCreated: Record<string, PopupFileRow | PopupFileRow[]> = {};
    issuesHavingNoFiles.forEach((issue: PopupFileRow) => {
      filesUnderWhichIssuesAreCreated[issue.fileId!] = {
        resourceType: "RESOURCE_TYPE_FILE",
        fileVersion: issue.fileId,
        name: issue.fileName,
      };
      if (issue.transmittalVersionId) {
        (filesUnderWhichIssuesAreCreated[issue.fileId!] as PopupFileRow).transmittalVersionId =
          issue.transmittalVersionId;
      }
    });
    setListOfSelectedFiles({
      ...listOfSelectedFiles,
      ...filesUnderWhichIssuesAreCreated,
    } as Record<string, PopupFileRow>);
    if (e) {
      e.stopPropagation();
    }
  };

  /**
   * Get selected files count
   */
  const selectedFilesCount = Object.keys(listOfSelectedFiles).length;

  /**
   * Check if there are any selected files
   */
  const hasSelectedFiles = selectedFilesCount > 0;

  /**
   * Get selected files as array
   */
  const getSelectedFilesArray = () => Object.values(listOfSelectedFiles);

  /**
   * Toggle between list view and folder structure view
   */
  const handleToggleFilesAsList = () => {
    setToggleFilesAsList((prev) => !prev);
  };

  /**
   * Handle space list fetch callback - sets warning if no spaces available
   */
  const handleSpaceListFetch = (response: { data?: { result?: { _id?: ObjectIdString; spaceName?: string }[] } }) => {
    if (!workspaceId && response.data?.result?.length === 0) {
      setShowNoSpaceWarning(true);
    } else {
      setShowNoSpaceWarning(false);
    }
  };

  /**
   * Handle space selection callback
   */
  const handleSpaceSelect = (space: { _id?: ObjectIdString; id?: ObjectIdString; spaceName?: string }) => {
    const spaceId = space?._id || space?.id;
    setSwitchSpaceId(spaceId);
  };

  /**
   * Check if view toggle should be shown
   * Use prop if provided, otherwise compute based on plans (only show when user has both PM and CDE plans)
   */
  const showViewToggle = hasPMPlan && hasCDEPlan && allowViewToggle;

  /**
   * Check if currently showing list view
   */
  const isListView = toggleFilesAsList;

  /**
   * Check if currently showing folder structure view
   */
  const isFolderView = !toggleFilesAsList;

  return {
    // Selection state
    listOfSelectedFiles,
    setListOfSelectedFiles,
    selectedFiles,
    setSelectedFiles,
    selectedFolderData,
    setSelectedFolderData,
    inputValue,
    setInputValue,
    issuesHavingNoFiles,
    setIssuesHavingNoFiles,

    // Button states
    AddToListDisabled,
    setAddToListDisabled,
    disableAttachButton,
    setDisableAttachButton,
    disableCreateShortcutButton,
    setDisableCreateShortcutButton,

    // View mode state
    toggleFilesAsList,
    setToggleFilesAsList,
    handleToggleFilesAsList,
    showViewToggle,
    isListView,
    isFolderView,

    // Space switching state
    switchSpaceId,
    setSwitchSpaceId,
    showNoSpaceWarning,
    setShowNoSpaceWarning,
    handleSpaceListFetch,
    handleSpaceSelect,

    // Selection handlers
    handleFileSelectionCallback,
    handleAddToSelection,
    handleRemoveOfSelectedFile,
    clearAllSelected,
    handleFileSelectedButtonClickCallback,
    handleFolderSelectionCallback,
    handleFolderSelectedCallback,
    removeIssuesWithNoFiles,
    acceptFilesForIssues,

    // Computed values
    selectedFilesCount,
    hasSelectedFiles,
    getSelectedFilesArray,
  };
}

export default useFileManagerAsPopupShared;
